import {
  Alert,
  Button,
  Checkbox,
  Link,
  Modal,
  PasswordInput,
  Stack,
  TextInput,
  tokens,
} from '@hotjar/hotsauce';
import { Trans, useTranslation, useHelpHotjarUrl } from '@hotjar/translations';
import { Controller } from 'react-hook-form';
import styled from '@emotion/styled';
import { StyledField } from '../styles';
import { useDeleteAccount } from './useDeleteAccount';

const StyledAlert = styled(Alert)`
  margin-bottom: ${tokens.spacer.x2pt5};
`;

export const DeleteAccountModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element => {
  const { t } = useTranslation('profile');
  const helpHotjarUrl = useHelpHotjarUrl();
  const { onSubmit, control, register, isSSOOnly, errors, isAccountAdmin, isLoading } =
    useDeleteAccount();

  return (
    <Modal
      contentLabel={t(`delete-account-modal.title`)}
      isOpen={isOpen}
      size="m"
      onClose={onClose}
    >
      <Modal.Header title={t(`delete-account-modal.title`)} />
      <Modal.Body>
        <StyledAlert kind="warning">
          <Trans t={t} i18nKey="delete-account-modal.p1">
            <Link
              kind="inline"
              href={`${helpHotjarUrl}/articles/115011640427-Data-Retention`}
              isExternal
            >
              here
            </Link>
          </Trans>
        </StyledAlert>

        <form id="delete-account-form" onSubmit={onSubmit}>
          <Stack preset="form">
            {t(`delete-account-modal.p2`, {
              emailOrPassword: isSSOOnly
                ? t('delete-account-modal.p2-email')
                : t('delete-account-modal.p2-password'),
            })}

            {isSSOOnly ? (
              <div data-testid="email">
                <StyledField
                  inputId="email"
                  labelText=""
                  invalidText={errors?.email?.message}
                  isInvalid={!!errors.email}
                >
                  <TextInput
                    id="email"
                    name="email"
                    isInvalid={!!errors.email}
                    ref={register({ required: t<string>('delete-account-modal.email-error') })}
                  />
                </StyledField>
              </div>
            ) : (
              <div data-testid="password">
                <StyledField
                  inputId="password"
                  labelText=""
                  invalidText={errors?.password?.message}
                  isInvalid={!!errors.password}
                >
                  <PasswordInput
                    id="password"
                    name="password"
                    isInvalid={!!errors.password}
                    ref={register({ required: t<string>('delete-account-modal.password-error') })}
                  />
                </StyledField>
              </div>
            )}

            <StyledField
              inputId="confirmDelete"
              labelText=""
              invalidText={errors?.confirmDelete?.message}
              isInvalid={!!errors.confirmDelete}
            >
              <Controller
                control={control}
                name="confirmDelete"
                rules={{
                  required: t<string>('delete-account-modal.confirm-delete-error'),
                }}
                render={({ value, onChange }) => (
                  <Checkbox
                    isChecked={value}
                    onChange={() => onChange(!(value as boolean))}
                    isInvalid={!!errors.confirmDelete}
                    labelContent={t('delete-account-modal.confirm-delete')}
                    id="confirmDelete"
                  />
                )}
              />
            </StyledField>

            {isAccountAdmin && (
              <StyledField
                inputId="confirmAccountDelete"
                labelText=""
                invalidText={errors?.confirmAccountDelete?.message}
                isInvalid={!!errors.confirmAccountDelete}
              >
                <Controller
                  control={control}
                  name="confirmAccountDelete"
                  rules={{
                    required: t<string>('delete-account-modal.confirm-account-delete-error'),
                  }}
                  render={({ value, onChange }) => (
                    <Checkbox
                      isChecked={value}
                      onChange={() => onChange(!(value as boolean))}
                      isInvalid={!!errors.confirmAccountDelete}
                      labelContent={<>{t('delete-account-modal.confirm-account-delete')}</>}
                      id="confirmAccountDelete"
                    />
                  )}
                />
              </StyledField>
            )}
          </Stack>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t(`delete-account-modal.cancel`)}
        </Button>
        <Button
          kind="danger"
          type="submit"
          form="delete-account-form"
          id="delete-submit-button"
          isLoading={isLoading}
        >
          {t(`delete-account-modal.delete`)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
